import { useState, useMemo, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Business, CustomAttr } from '@turizi/shared';
import { BUSINESSES_PATH } from '@/lib/navigation';
import { Button, SegmentedControl, Popover } from '@mantine/core';
import { IconX, IconFilter } from '@tabler/icons-react';
import { BusinessToString } from '@/lib/models-utils';
import { updateUrlParams, getUrlParam } from '@/lib/format-utils';
import { Link } from 'react-router-dom';
import { localTenant } from '@/services/session-service';
import BusinessFilters from '@/components/business-filters';
import { SkeletonList } from '@/components/snippets';
import BusinessCardPrivate from '@/components/business-card-private';
import CustomCheckbox from '@/components/custom-checkbox';
import SearchInput from '@/components/search-input';
import BusinessesTable from '@/components/businesses-table';

interface FilteredBusinessesLayoutProps {
    businesses: Business[];
    isLoading: boolean;
    selectedIds: string[];
    onSelectionChange: (ids: string[]) => void;
    renderActions?: (selectedBusinesses: Business[], clearSelection: () => void) => React.ReactNode;
    renderBusinessExtra?: (business: Business) => React.ReactNode;
}

const FilteredBusinessesLayout = memo(function FilteredBusinessesLayout({
    businesses,
    isLoading,
    selectedIds,
    onSelectionChange,
    renderActions,
    renderBusinessExtra
}: FilteredBusinessesLayoutProps): JSX.Element {
    const { t } = useTranslation();
    const tenant = localTenant();

    const [searchText, setSearchText] = useState(getUrlParam('search', ''));
    const [filtersOpened, setFiltersOpened] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' | null }>({
        key: 'name',
        direction: 'asc'
    });
    const [view, setView] = useState<'table' | 'list'>((getUrlParam('view', 'list') as 'table' | 'list'));
    const [businessFilters, setBusinessFilters] = useState({
        category: [] as string[],
        discount_category: [] as string[],
        publish_prices: [] as boolean[],
        discount: [] as number[],
        country_code: [] as string[],
        city: [] as string[],
        my_companies: [] as boolean[]
    });
    const [attrFilters, setAttrFilters] = useState<Record<string, string[]>>({});

    useEffect(() => {
        updateUrlParams({
            search: searchText,
            view: view
        });
    }, [searchText, view]);

    const applyFilters = useCallback((orgs: any[]) => {
        return orgs
            .filter(org => {
                if (businessFilters.category.length > 0 &&
                    (!org.category || !businessFilters.category.includes(org.category))) {
                    return false;
                }
                if (businessFilters.discount_category.length > 0 &&
                    (!org.tenant_business?.discount_category || !businessFilters.discount_category.includes(org.tenant_business.discount_category))) {
                    return false;
                }
                if (businessFilters.publish_prices.length > 0) {
                    const includeWithPrices = businessFilters.publish_prices.includes(true);
                    const includeWithoutPrices = businessFilters.publish_prices.includes(false);
                    const orgPublishPrices = org.tenant_business?.publish_prices || false;
                    if (includeWithPrices && !includeWithoutPrices && !orgPublishPrices) return false;
                    if (!includeWithPrices && includeWithoutPrices && orgPublishPrices) return false;
                }
                if (businessFilters.country_code.length > 0 &&
                    (!org.country_code || !businessFilters.country_code.includes(org.country_code))) {
                    return false;
                }
                if (businessFilters.city.length > 0 &&
                    (!org.city || !businessFilters.city.includes(org.city))) {
                    return false;
                }
                if (businessFilters.my_companies.length > 0 && businessFilters.my_companies.includes(true)) {
                    if (org.owner_id !== tenant?.id) {
                        return false;
                    }
                }
                return true;
            })
            .filter(org => {
                const orgString = BusinessToString(org, t);
                const searchWords = searchText.toLocaleLowerCase().split(' ').filter(word => word.length > 0);
                return searchWords.every(word => orgString.includes(word));
            })
            .filter(business => {
                for (const [attrKey, selectedValues] of Object.entries(attrFilters)) {
                    if (selectedValues.length === 0) continue;
                    const businessAttrValue = business.self_attrs?.find((attr: any) => attr.key === attrKey)?.value;
                    if (businessAttrValue === undefined || businessAttrValue === null) return false;
                    if (!selectedValues.includes(String(businessAttrValue))) return false;
                }
                return true;
            });
    }, [businessFilters, tenant, searchText, attrFilters, t]);

    const applySort = useCallback((businessesList: Business[]) => {
        return businessesList.sort((a, b) => {
            let aValue: any;
            let bValue: any;
            switch (sortConfig.key) {
                case 'name':
                    aValue = a.name || '';
                    bValue = b.name || '';
                    break;
                case 'category':
                    aValue = a.category || '';
                    bValue = b.category || '';
                    break;
                case 'discount_category':
                    aValue = a.tenant_business?.discount_category || '';
                    bValue = b.tenant_business?.discount_category || '';
                    break;
                case 'city':
                    aValue = a.city || '';
                    bValue = b.city || '';
                    break;
                case 'country_code':
                    aValue = a.country_code || '';
                    bValue = b.country_code || '';
                    break;
                default:
                    return 0;
            }
            if (!aValue && !bValue) return 0;
            if (!aValue) return 1;
            if (!bValue) return -1;
            const comparison = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase());
            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
    }, [sortConfig]);

    const filteredBusinesses = useMemo(() => applySort(applyFilters([...businesses])), [businesses, applyFilters, applySort]);

    const attrValues = useMemo(() => {
        const values: Record<string, Set<string>> = {};
        const categoryFilteredBusinesses = businessFilters.category.length > 0
            ? (businesses || []).filter((business: Business) => business.category && businessFilters.category.includes(business.category))
            : (businesses || []);
        categoryFilteredBusinesses.forEach((business: Business) => {
            business.self_attrs?.forEach((attr: CustomAttr) => {
                if (!attr.key || attr.value === undefined || attr.value === null) return;
                if (!values[attr.key]) values[attr.key] = new Set();
                values[attr.key].add(String(attr.value));
            });
        });
        const result: Record<string, string[]> = {};
        Object.entries(values).forEach(([key, valueSet]) => {
            result[key] = Array.from(valueSet);
        });
        return result;
    }, [businesses, businessFilters.category]);

    const filterOptions = useMemo(() => {
        const categories = new Set<string>();
        const discountCategories = new Set<string>();
        const discounts = new Set<number>();
        const countryCodes = new Set<string>();
        const cities = new Set<string>();
        (businesses || []).forEach((business: Business) => {
            if (business.category) categories.add(business.category);
            if (business.tenant_business?.discount_category) discountCategories.add(business.tenant_business.discount_category);
            if (business.country_code) countryCodes.add(business.country_code);
            if (business.city) cities.add(business.city);
        });
        return {
            categories: Array.from(categories).sort(),
            discountCategories: Array.from(discountCategories).sort(),
            discounts: Array.from(discounts).sort((a, b) => a - b),
            countryCodes: Array.from(countryCodes).sort(),
            cities: Array.from(cities).sort()
        };
    }, [businesses]);

    const handleAttrFilterChange = (attrKey: string, values: string[]) => {
        setAttrFilters(prev => ({ ...prev, [attrKey]: values }));
    };

    const handleFilterChange = (filterType: string, value: any) => {
        setBusinessFilters(prev => ({ ...prev, [filterType]: value }));
    };

    const clearAllFilters = useCallback(() => {
        setBusinessFilters({
            category: [], discount_category: [], publish_prices: [], discount: [], country_code: [], city: [], my_companies: []
        });
        setAttrFilters({});
    }, []);

    const toggleFilter = useCallback((filterType: string, value: any) => {
        setBusinessFilters(prev => {
            const currentFilter = (prev[filterType as keyof typeof prev] || []) as any[];
            const updatedFilter = currentFilter.includes(value)
                ? currentFilter.filter((item: any) => item !== value)
                : [...currentFilter, value];
            return { ...prev, [filterType]: updatedFilter };
        });
    }, []);

    const getFilterLabel = useCallback((key: string): string => {
        if (key === 'category') return t('type');
        if (key === 'discount_category') return t('discount_category');
        if (key === 'publish_prices') return t('publish_prices');
        if (key === 'country_code') return t('country');
        if (key === 'city') return t('city');
        return t(key);
    }, [t]);

    const formatFilterValue = useCallback((value: any): string => {
        if (typeof value === 'boolean') return value ? t('yes') : t('no');
        return t(String(value));
    }, [t]);

    const activeFiltersCount = useMemo(() => {
        const allFilters = { ...businessFilters, ...attrFilters };
        return Object.values(allFilters).reduce((count, filterValues) => count + filterValues.length, 0);
    }, [businessFilters, attrFilters]);

    useEffect(() => {
        onSelectionChange([]);
    }, [businessFilters, attrFilters, searchText]);

    const toggleBusinessSelection = useCallback((businessId: string) => {
        if (selectedIds.includes(businessId)) {
            onSelectionChange(selectedIds.filter(id => id !== businessId));
        } else {
            onSelectionChange([...selectedIds, businessId]);
        }
    }, [selectedIds, onSelectionChange]);

    const clearSelection = () => onSelectionChange([]);

    if (isLoading) {
        return <SkeletonList />;
    }

    return (
        <div className="space-y-4 min-w-0">
            {/* Top Toolbar: Search bar, Filters Popover and View toggle */}
            <div className="flex gap-3 items-center flex-wrap sm:flex-nowrap">
                <div className="flex-grow min-w-[200px]">
                    <SearchInput
                        size="md"
                        placeholder={t('search')}
                        initialValue={searchText}
                        onSearch={setSearchText}
                    />
                </div>
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                    <Popover
                        opened={filtersOpened}
                        onChange={setFiltersOpened}
                        position="bottom-end"
                        withArrow
                        shadow="md"
                        width={360}
                        styles={{ dropdown: { padding: '12px', borderRadius: '12px', maxHeight: '500px', overflowY: 'auto' } }}
                    >
                        <Popover.Target>
                            <Button
                                variant="subtle"
                                color="black"
                                leftSection={<IconFilter size={16} />}
                                onClick={() => setFiltersOpened(prev => !prev)}
                                size="sm"
                            >
                                {t('filters')} {activeFiltersCount > 0 ? `(${activeFiltersCount})` : ''}
                            </Button>
                        </Popover.Target>
                        <Popover.Dropdown onClick={e => e.stopPropagation()}>
                            <BusinessFilters
                                filters={businessFilters}
                                attrFilters={attrFilters}
                                filterOptions={filterOptions}
                                attrValues={attrValues}
                                onFilterChange={handleFilterChange}
                                onAttrFilterChange={handleAttrFilterChange}
                                onClearAll={clearAllFilters}
                            />
                        </Popover.Dropdown>
                    </Popover>

                    <SegmentedControl
                        value={view}
                        onChange={(val: any) => setView(val)}
                        size="sm"
                        color="black"
                        data={[
                            { value: 'list', label: t('list') },
                            { value: 'table', label: t('table') },
                        ]}
                    />
                </div>
            </div>

            {/* Selection & Count Summary Row */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                    <div className="text-xs text-gray-500 font-medium">
                        {filteredBusinesses.length} {t('businesses')}
                    </div>
                    {selectedIds.length > 0 && (
                        <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-md border border-blue-200">
                            {selectedIds.length} {t('selected')}
                        </span>
                    )}
                    {selectedIds.length !== filteredBusinesses.length && (
                        <Button
                            onClick={() => onSelectionChange(filteredBusinesses.map(s => String(s.id)))}
                            variant="subtle"
                            color="black"
                            size="compact-xs"
                            className="hover:bg-gray-200 text-gray-700"
                        >
                            {t('select_all')}
                        </Button>
                    )}
                    {selectedIds.length > 0 && (
                        <Button
                            onClick={clearSelection}
                            variant="subtle"
                            color="red"
                            size="compact-xs"
                            className="hover:bg-red-50 text-red-600"
                        >
                            {t('clear')}
                        </Button>
                    )}
                </div>
                {renderActions && renderActions(
                    filteredBusinesses.filter(b => selectedIds.includes(String(b.id))),
                    clearSelection
                )}
            </div>

            {/* Active Filter Pills */}
            {activeFiltersCount > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                    {Object.entries(businessFilters).map(([filterKey, filterValues]) =>
                        filterValues.map((value) => (
                            <div
                                key={`${filterKey}-${value}`}
                                className="text-xs solid-round-red-hover flex gap-2 justify-between items-center py-1 px-2 cursor-pointer select-none"
                                onClick={() => toggleFilter(filterKey, value)}
                            >
                                <div className="flex items-center gap-1">
                                    <div className="font-light">{getFilterLabel(filterKey)}:</div>
                                    <div className="font-semibold">{formatFilterValue(value)}</div>
                                </div>
                                <IconX size={12} stroke={3} color='#aaa' />
                            </div>
                        ))
                    )}
                    {Object.entries(attrFilters).map(([attrKey, attrValues]) =>
                        attrValues.map((value) => (
                            <div
                                key={`${attrKey}-${value}`}
                                className="text-xs solid-round-red-hover flex gap-2 justify-between items-center py-1 px-2 cursor-pointer select-none"
                                onClick={() => {
                                    const currentValues = attrFilters[attrKey] || [];
                                    handleAttrFilterChange(attrKey, currentValues.filter(v => v !== value));
                                }}
                            >
                                <div className="flex items-center gap-1">
                                    <div className="font-light">{getFilterLabel(attrKey)}:</div>
                                    <div className="font-semibold">{value}</div>
                                </div>
                                <IconX size={12} stroke={3} color='#aaa' />
                            </div>
                        ))
                    )}
                    <Button variant="light" size="compact-xs" radius="xl" color="black" onClick={clearAllFilters}>
                        {t('clear_filters')}
                    </Button>
                </div>
            )}

            {/* Main Content Area: Table vs List */}
            {view === 'table' ? (
                <BusinessesTable
                    businesses={filteredBusinesses}
                    selectedIds={selectedIds}
                    toggleBusinessSelection={toggleBusinessSelection}
                    enableBulkActions={true}
                />
            ) : (
                <div className='space-y-1'>
                    {filteredBusinesses?.map((business, index) => (
                        <div
                            key={index}
                            className="flex gap-2 items-start">
                            <div className="mt-2">
                                <CustomCheckbox
                                    checked={selectedIds.includes(String(business.id))}
                                    onChange={() => toggleBusinessSelection(String(business.id))}
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <Link
                                    to={`${BUSINESSES_PATH}/${business.id}`}
                                    className="block w-full"
                                    style={{ textDecoration: 'none', color: 'inherit' }}>
                                    <BusinessCardPrivate
                                        business={business}
                                        isLoading={false}
                                        showTariffsLink={false}
                                    />
                                </Link>
                                {renderBusinessExtra && renderBusinessExtra(business)}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export default FilteredBusinessesLayout;
